import { Box, TextField } from '@material-ui/core';
import { Channel, getMerchantStructureByMerchantId, User, UserType } from '@nemuru-eng/js-api-client';
import { useParams } from 'react-router-dom';

import { ChannelTypeIcons } from '@/components/modules/MerchantDetail/MerchantStructure/constants/ChannelTypeIcons';
import { ChangeUserConfirmationDialog } from '@/components/shared/components/UserList/components/UserTable/components/UserTableBody/components/UserActionsRowDialog/components/ChangeUser/components/ChangeUserConfirmationDialog';
import { NoUsersFound } from '@/components/shared/components/UserList/components/UserTable/components/UserTableBody/components/UserActionsRowDialog/components/ChangeUser/components/NoUsersFound';
import { PaginatedListWrapper } from '@/components/shared/components/UserList/components/UserTable/components/UserTableBody/components/UserActionsRowDialog/components/ChangeUser/components/PaginatedListWrapper';
import { SelectedUserStructure } from '@/components/shared/components/UserList/components/UserTable/components/UserTableBody/components/UserActionsRowDialog/components/ChangeUser/components/SelectedUserStructure';
import { UserInStructureCard } from '@/components/shared/components/UserList/components/UserTable/components/UserTableBody/components/UserActionsRowDialog/components/ChangeUser/components/UserInStructureCard';
import { useChangeUser } from '@/components/shared/components/UserList/components/UserTable/components/UserTableBody/components/UserActionsRowDialog/components/ChangeUser/hooks/useChangeUser';
import { useChangeUserSearch } from '@/components/shared/components/UserList/components/UserTable/components/UserTableBody/components/UserActionsRowDialog/components/ChangeUser/hooks/useChangeUserSearch';
import { UserRowActionType } from '@/components/shared/components/UserList/types/UserRowActionType';
import { useCustomPagination } from '@/components/shared/hooks/useCustomPagination';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { ICON_NAMES } from '@/config/config';
import { useCache } from '@/hooks/requests/useCache';

interface ChangeUserProps {
  userId: string;
  selectedUser: User;
  opened: boolean;
  setOpened: (open: boolean) => void;
  handleClose: () => void;
}

export const ChangeUser = ({ userId, selectedUser, opened, setOpened, handleClose }: ChangeUserProps) => {
  const { merchantId } = useParams();

  if (!merchantId) return null;

  const { data: merchantStructure } = useCache(getMerchantStructureByMerchantId, { merchantId });

  const { getTextIdTranslation } = useTranslation();

  if (!merchantStructure) return <></>;

  const { searchResult, selectedDestination, setSearchString, setSelectedDestination, minimumSearchStringForResults } =
    useChangeUserSearch({
      merchantStructure,
      selectedUser,
    });

  const { onSubmit, onConfirmChange, confirmationDialogOpened, setConfirmationDialog, loading } = useChangeUser({
    userId,
    selectedDestination,
    selectedUser,
    handleCloseDialog: handleClose,
  });

  const { page, setPage, rowsPerPage, onNext, onPrev, disableNext, disablePrev } = useCustomPagination({
    filteredResults: searchResult,
    rowsPerPage: 4,
  });

  const currentAction = UserRowActionType.CHANGE_USER;

  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      buttons={
        <>
          <StandardButton disabled={loading} onClick={handleClose}>
            <Text tId={'CANCEL'} />
          </StandardButton>
          <StandardButton loading={loading} disabled={!selectedDestination} main onClick={onSubmit}>
            <Text tId={`${currentAction}_BTN`} />
          </StandardButton>
        </>
      }
    >
      <Box mb={4}>
        <Font variant="h3" color="title">
          <Text tId={currentAction} />
        </Font>
      </Box>
      <Box>
        <Box pb={2}>
          <Font variant={'body2'} color={'textPrimary'}>
            <Text tId={'YOU_WILL_CHANGE_THE_FOLLOWING_USER'} />
          </Font>
        </Box>
        <SelectedUserStructure selectedUser={selectedUser} merchantStructure={merchantStructure} />
        <Box width={'100%'} py={1}>
          <TextField
            label={<Text tId={'SELECT_WHERE_TO_MOVE'} />}
            placeholder={getTextIdTranslation('SEARCH_FOR_MERCHANT_CHANNEL_AGENT_NAME') as string}
            fullWidth
            onChange={(e) => {
              setSearchString(e.target.value.toLowerCase());
              setPage(1);
            }}
          />
        </Box>
        <Box pb={2} minHeight={324}>
          {minimumSearchStringForResults && (
            <Box className="appear-anim">
              {searchResult && searchResult?.length > 0 ? (
                <PaginatedListWrapper
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalElementsValue={searchResult?.length}
                  onNext={onNext}
                  onPrev={onPrev}
                  disableNext={disableNext}
                  disablePrev={disablePrev}
                  boxProps={{ height: 'auto', paddingBottom: '65px', maxHeight: '280px' }}
                >
                  <>
                    {searchResult
                      .filter((_, i) => i >= page * rowsPerPage - rowsPerPage && i < page * rowsPerPage)
                      .map((result) => {
                        const userType =
                          'channels' in result && result.businessName
                            ? UserType.MERCHANT
                            : 'description' in result
                            ? UserType.CHANNEL
                            : UserType.AGENT;
                        return (
                          <UserInStructureCard
                            id={result.id}
                            user={result}
                            type={userType}
                            merchantStructure={merchantStructure}
                            isSelected={selectedDestination?.id === result?.id}
                            onClick={() =>
                              selectedDestination?.id === result?.id
                                ? setSelectedDestination(undefined)
                                : setSelectedDestination(result)
                            }
                            iconName={
                              userType === UserType.MERCHANT
                                ? 'corporate_fare'
                                : userType === UserType.CHANNEL
                                ? ChannelTypeIcons[(result as Channel).type]
                                : ICON_NAMES.AGENT
                            }
                          />
                        );
                      })}
                  </>
                </PaginatedListWrapper>
              ) : (
                <NoUsersFound />
              )}
            </Box>
          )}
        </Box>
      </Box>
      {confirmationDialogOpened && selectedDestination && (
        <ChangeUserConfirmationDialog
          onConfirmChange={onConfirmChange}
          confirmationDialogOpened={confirmationDialogOpened}
          setConfirmationDialog={setConfirmationDialog}
          selectedUser={selectedUser}
          selectedDestination={selectedDestination}
          loading={loading}
        />
      )}
    </NemuruDialog>
  );
};
